class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ============================================================
   Free Shipping Progress Bar — drawer + cart page + product page
   ============================================================ */
(function () {
  const CONFIG = {
    regular: 400000,   // 4.000 RSD x 100
    bulky: 1300000,    // 13.000 RSD x 100
    tag: 'kabasto',
  };

  const _tagCache = {};

  async function _getProductTags(handle) {
    if (_tagCache[handle] !== undefined) return _tagCache[handle];
    try {
      const res = await fetch(`/products/${handle}.json`);
      if (!res.ok) { _tagCache[handle] = []; return []; }
      const data = await res.json();
      const raw = data.product?.tags || '';
      _tagCache[handle] = typeof raw === 'string'
        ? raw.split(',').map((t) => t.trim()).filter(Boolean)
        : raw;
      return _tagCache[handle];
    } catch {
      _tagCache[handle] = [];
      return [];
    }
  }

  async function _hasKabasto(items) {
    const handles = [...new Set(items.map((i) => i.handle).filter(Boolean))];
    const allTags = await Promise.all(handles.map(_getProductTags));
    return allTags.some((tags) => tags.includes(CONFIG.tag));
  }

  function _fmt(cents) {
    return (cents / 100).toLocaleString('sr-Latn-RS') + ' RSD';
  }

  function _renderBar(bar, total, threshold, isKabasto, variant) {
    const progress = Math.min((total / threshold) * 100, 100);
    const remaining = Math.max(threshold - total, 0);
    const done = total >= threshold;
    const typeLabel = isKabasto ? 'kabaste robe' : 'porudžbine';

    bar.className = 'free-shipping-bar'
      + (done ? ' free-shipping-bar--complete' : '')
      + (variant === 'inline' ? ' free-shipping-bar--inline' : '')
      + (variant === 'page' ? ' free-shipping-bar--page' : '');

    const pct = progress.toFixed(1);
    // PDP redizajn (checklist §0/§6): 'inline' varijanta (product page) koristi
    // rose #F72585 dok napreduje; drawer/cart-page ostaju van scope-a ovog
    // redizajna i zadržavaju zeleni fill. "Complete" stanje ostaje zeleno
    // svuda (univerzalni signal uspeha).
    const inProgressColor = variant === 'inline' ? '#f72585' : '#43a047';
    const trackBgIdle = variant === 'inline' ? '#fbcfe4' : '#c8e6c9';
    const fillColor = done ? '#2e7d32' : inProgressColor;
    const trackBg = done
      ? fillColor
      : `linear-gradient(to right, ${fillColor} ${pct}%, ${trackBgIdle} ${pct}%)`;

    bar.innerHTML = `
      <p class="free-shipping-bar__text">
        ${done
          ? '🎉 Besplatna isporuka je uključena!'
          : `Do besplatne isporuke ${typeLabel} nedostaje još <strong>${_fmt(remaining)}</strong>`
        }
      </p>
      <div class="free-shipping-bar__track" style="background:${trackBg}"><span></span></div>
    `;
  }

  // Generički: fetch /cart.js, nađi ili kreiraj bar element, renderuj
  async function _fetchAndRender(barId, getAnchor, insertFn, variant) {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      if (!cart.items || cart.items.length === 0) {
        document.getElementById(barId)?.remove();
        return;
      }

      const anchor = getAnchor();
      if (!anchor) return;

      let bar = document.getElementById(barId);
      if (!bar) {
        bar = document.createElement('div');
        bar.id = barId;
        insertFn(anchor, bar);
      }

      const kabasto = await _hasKabasto(cart.items);
      const threshold = kabasto ? CONFIG.bulky : CONFIG.regular;
      _renderBar(bar, cart.total_price, threshold, kabasto, variant);
    } catch (e) {
      console.warn('[FreeShippingBar]', e);
    }
  }

  // --- Placement 1: Cart drawer ---
  function _updateDrawerBar() {
    _fetchAndRender(
      'free-shipping-bar',
      () => document.querySelector('#CartDrawer .drawer__inner'),
      (anchor, bar) => anchor.prepend(bar),
      'drawer'
    );
  }

  // --- Placement 2: Cart page (/cart) ispod naslova ---
  function _updateCartPageBar() {
    if (!window.location.pathname.startsWith('/cart')) return;
    _fetchAndRender(
      'free-shipping-bar-page',
      () => document.querySelector('.title-wrapper-with-link'),
      (anchor, bar) => anchor.after(bar),
      'page'
    );
  }

  // --- Placement 3: Product page ispod CTA (buy buttons) ---
  // PDP redizajn: bar ide ispod "Dodaj u korpu"/"Kupi odmah" (unutar
  // buy-box kartice), ne ispod cene. Fallback na staro sidro (cena)
  // ako observe wrapper ne postoji (npr. starije verzije sekcije).
  function _updateProductPageBar() {
    _fetchAndRender(
      'free-shipping-bar-inline',
      () => document.querySelector('[id^="pdp-buy-buttons-observe-"]')
        || document.querySelector('[id^="price-template"]'),
      (anchor, bar) => anchor.after(bar),
      'inline'
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    // === Hook 1: Drawer otvoren sa postojećom korpom ===
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      const _origOpen = drawerEl.open.bind(drawerEl);
      drawerEl.open = function (...args) {
        _origOpen(...args);
        setTimeout(_updateDrawerBar, 50);
      };
    }

    // === Hook 2: Proizvod dodat (CartDrawer.renderContents) ===
    const _origRenderContents = CartDrawer.prototype.renderContents;
    CartDrawer.prototype.renderContents = function (parsedState) {
      _origRenderContents.call(this, parsedState);
      setTimeout(() => {
        _updateDrawerBar();
        _updateProductPageBar(); // osveži inline bar nakon add-to-cart
      }, 50);
    };

    // === Statički init na trenutnoj strani ===
    _updateCartPageBar();
    _updateProductPageBar();

    // === Hook 3: qty/remove izmene (drawer i cart page) ===
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'cart-items') return;
        setTimeout(() => {
          _updateDrawerBar();
          _updateCartPageBar();
          _updateProductPageBar();
        }, 50);
      });
    }
  });
})();
